// One assessment form, open for editing.
//
// Order of operations for every edit:
//   1. React state updates (the field shows the new value immediately)
//   2. the record is written to device storage (encrypted) -- this is what
//      makes the edit survive a closed tab, a dead battery or lost signal
//   3. a sync to the server is scheduled a moment later, batching keystrokes
//
// Sync also runs when the browser comes back online, when the tab is hidden,
// and on a slow interval (to pick up a manager's edits or a status change).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assessmentApi } from '../services/assessmentApi';
import { getCache, loadForm, putCache, requestPersistence, saveForm } from '../services/assessmentStore';
import {
  classifyError, currentValues, isLocked, lockRecord, pushRecord, recordFromServer, reconcile,
} from '../services/assessmentSync';

const SYNC_DEBOUNCE_MS = 1500;
const REFRESH_INTERVAL_MS = 60000;
const RETRY_DELAYS_MS = [5000, 15000, 30000, 60000];

export async function loadSchema(code, version) {
  const cacheId = `schema:${code}:${version || 'current'}`;
  try {
    const cached = await getCache(cacheId);
    if (cached) return cached;
  } catch { /* fall through to the network */ }
  const schema = await assessmentApi.schema(code, version);
  putCache(`schema:${code}:${schema.version}`, schema).catch(() => {});
  return schema;
}

export function useAssessmentForm(uuid, userId) {
  const [record, setRecord] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [sync, setSync] = useState({ state: 'idle', lastSyncedAt: null, message: null });
  const [local, setLocal] = useState({ state: 'saved', available: true });

  const recordRef = useRef(null);
  const localOk = useRef(true);
  const writesInFlight = useRef(0);
  const syncing = useRef(null);
  const resyncWanted = useRef(false);
  const syncTimer = useRef(null);
  const retryIndex = useRef(0);
  const mounted = useRef(true);

  // -- local persistence ----------------------------------------------------

  // Coalesced: while one encrypted write is running, further edits only
  // replace `nextWrite`, and the loop writes the newest copy when it is free.
  // Fast typing costs one write at a time, never a backlog.
  const nextWrite = useRef(null);
  const writing = useRef(null);

  const persist = useCallback((next) => {
    if (!localOk.current || !userId) return Promise.resolve();
    nextWrite.current = next;
    if (writing.current) return writing.current;
    writesInFlight.current = 1;
    setLocal((s) => ({ ...s, state: 'saving' }));
    writing.current = (async () => {
      try {
        while (nextWrite.current) {
          const rec = nextWrite.current;
          nextWrite.current = null;
          await saveForm(userId, rec);
        }
        if (mounted.current) setLocal((s) => ({ ...s, state: 'saved' }));
      } catch (err) {
        console.error('Could not save assessment on this device', err);
        localOk.current = false;
        if (mounted.current) setLocal({ state: 'error', available: false });
      } finally {
        writesInFlight.current = 0;
        writing.current = null;
      }
    })();
    return writing.current;
  }, [userId]);

  const commit = useCallback((next) => {
    recordRef.current = next;
    if (mounted.current) setRecord(next);
    return persist(next);
  }, [persist]);

  // -- server sync ----------------------------------------------------------

  // runSync and scheduleSync call each other; going through a ref keeps both
  // stable without a dependency cycle.
  const runSyncRef = useRef(() => null);
  const scheduleSync = useCallback((delay = SYNC_DEBOUNCE_MS) => {
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => { runSyncRef.current(); }, delay);
  }, []);

  const runSync = useCallback(async () => {
    if (syncing.current) {
      resyncWanted.current = true;
      return syncing.current;
    }
    const rec = recordRef.current;
    if (!rec) return null;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setSync((s) => ({ ...s, state: 'offline' }));
      return null;
    }

    const sent = { ...(rec.pending || {}) };
    setSync((s) => ({ ...s, state: 'syncing' }));
    syncing.current = (async () => {
      try {
        const detail = await pushRecord(rec, sent);
        const next = reconcile(recordRef.current, sent, detail);
        await commit(next);
        retryIndex.current = 0;
        const invalid = Object.keys(detail.merge?.invalid || {});
        if (mounted.current) {
          setSync({
            state: 'synced',
            lastSyncedAt: next.lastSyncedAt,
            message: invalid.length ? `${invalid.length} answer(s) were not accepted by the server` : null,
          });
        }
        return next;
      } catch (err) {
        const kind = classifyError(err);
        if (kind === 'locked') {
          await commit(lockRecord(recordRef.current, err.body));
        }
        if (mounted.current) setSync((s) => ({ ...s, state: kind, message: err.message }));
        if (kind === 'offline' || kind === 'error') {
          const delay = RETRY_DELAYS_MS[Math.min(retryIndex.current, RETRY_DELAYS_MS.length - 1)];
          retryIndex.current += 1;
          scheduleSync(delay);
        }
        return null;
      } finally {
        syncing.current = null;
        if (resyncWanted.current) {
          resyncWanted.current = false;
          scheduleSync(300);
        }
      }
    })();
    return syncing.current;
  }, [commit, scheduleSync]);
  runSyncRef.current = runSync;

  // -- load -----------------------------------------------------------------

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    requestPersistence();

    (async () => {
      let rec = null;
      try {
        rec = await loadForm(userId, uuid);
      } catch (err) {
        localOk.current = false;
        setLocal({ state: 'error', available: false });
      }
      if (cancelled) return;
      if (rec) {
        recordRef.current = rec;
        setRecord(rec);
      } else {
        try {
          rec = recordFromServer(await assessmentApi.get(uuid));
          if (cancelled) return;
          await commit(rec);
        } catch (err) {
          if (!cancelled) setLoadError(err);
          return;
        }
      }
      try {
        const s = await loadSchema(rec.formCode, rec.schemaVersion);
        if (!cancelled) setSchema(s);
      } catch (err) {
        if (!cancelled) setLoadError(err);
        return;
      }
      if (!cancelled) runSync();
    })();

    return () => {
      cancelled = true;
      mounted.current = false;
      clearTimeout(syncTimer.current);
    };
  }, [uuid, userId]);

  // A form started on this device learns its definition version from the
  // server on first sync; reload the definition if it differs.
  useEffect(() => {
    if (!record?.schemaVersion || !schema || schema.version === record.schemaVersion) return;
    loadSchema(record.formCode, record.schemaVersion).then(setSchema).catch(() => {});
  }, [record?.schemaVersion, record?.formCode, schema]);

  // -- triggers -------------------------------------------------------------

  useEffect(() => {
    const onOnline = () => { retryIndex.current = 0; runSync(); };
    const onVisibility = () => { if (document.visibilityState === 'hidden') runSync(); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(() => { if (document.visibilityState === 'visible') runSync(); },
      REFRESH_INTERVAL_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [runSync]);

  // Only warn on leaving when an edit genuinely is not safe yet: device
  // storage failed (or is mid-write) and the server has not confirmed it.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const unsynced = Object.keys(recordRef.current?.pending || {}).length > 0;
      if (unsynced && (!localOk.current || writesInFlight.current > 0)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // -- actions --------------------------------------------------------------

  const setValue = useCallback((key, value) => {
    const rec = recordRef.current;
    if (!rec || isLocked(rec)) return;
    commit({ ...rec, pending: { ...(rec.pending || {}), [key]: { v: value, t: Date.now() } } });
    scheduleSync();
  }, [commit, scheduleSync]);

  // Server actions need every local edit on the server first, otherwise
  // "complete" would be judged against stale answers.
  // A few passes, because an edit typed during one sync is sent by the next.
  const flush = useCallback(async () => {
    for (let pass = 0; pass < 3; pass += 1) {
      if (syncing.current) await syncing.current;
      clearTimeout(syncTimer.current);
      resyncWanted.current = false;
      const result = await runSync();
      const rec = recordRef.current;
      if (rec?.server && !Object.keys(rec.pending || {}).length) return rec;
      if (!result) break;
    }
    throw new Error('You need to be online to do this. Your answers are saved on this device.');
  }, [runSync]);

  const serverAction = useCallback(async (fn) => {
    await flush();
    const detail = await fn(uuid);
    const next = { ...recordRef.current, server: detail, lastSyncedAt: Date.now() };
    await commit(next);
    return detail;
  }, [flush, uuid, commit]);

  const complete = useCallback(() => serverAction(assessmentApi.complete), [serverAction]);
  const reopen = useCallback(() => serverAction(assessmentApi.reopen), [serverAction]);

  const dismissLockedNotice = useCallback(() => {
    const rec = recordRef.current;
    if (rec?.lockedNotice) commit({ ...rec, lockedNotice: undefined });
  }, [commit]);

  const values = useMemo(() => currentValues(record), [record]);

  return {
    record,
    schema,
    values,
    loadError,
    sync,
    local,
    pendingCount: Object.keys(record?.pending || {}).length,
    locked: isLocked(record),
    setValue,
    syncNow: runSync,
    complete,
    reopen,
    dismissLockedNotice,
  };
}
